import React, { Component, createRef } from "react";
import { observer } from "mobx-react";
import { action, computed } from "mobx";
import PropTypes from "prop-types";
import { Elements, ElementsConsumer } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Button, Card, Form, Icon, Ref, Segment, Select, Table } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import OtherProductCard from "~/modules/BillingCentral/components/OtherProductCard";
import OtherProductStore from "~/stores/OtherProductStore";
import FormStore from "~/stores/FormStore";
import SubscriptionEditFormTableRow from "~/modules/BillingCentral/components/SubscriptionEditFormTableRow";
import CommonHelper from "~/utils/CommonHelper";
import StickyMenu from "~/components/StickyMenu";
import SubscriptionStore from "~/stores/Subscription/SubscriptionStore";
import CreditCardFormStore from "~/stores/Credit/CreditCardFormStore";
import CreditHelper from "~/utils/CreditHelper";
import NoCreditCardsMessage from "~/modules/BillingCentral/components/NoCreditCardsMessage";
import NoCreditCardSelectedMessage from "~/modules/BillingCentral/components/NoCreditCardSelectedMessage";
import NoDefaultCreditCardMessage from "~/modules/BillingCentral/components/NoDefaultCreditCardMessage";
import CreditCardForm from "~/modules/BillingCentral/components/CreditCardForm";
import ToastMessageStore from "~/stores/ToastMessageStore";
import CustomerStore from "~/stores/CustomerStore";
import ProviderStore from "~/stores/Provider/ProviderStore";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";

@observer
class SubscriptionEditForm extends Component {
  ref = null;

  stripeObject = null;

  constructor(props) {
    super(props);

    this.ref = createRef();

    const { stripeApiPublishableKey, creditCardFormStore } = props;

    this.stripePromise = loadStripe(stripeApiPublishableKey);
    this.stripePromise.then(result => {
      this.stripeObject = result;
    });

    const { creditCardStore } = creditCardFormStore;

    this.state = {
      addCreditCardFormVisible: creditCardStore.creditCards.length === 0,
    };
  }

  @computed
  get selectedOtherProductIds() {
    const selectedOtherProductIds = [];

    // Check the form
    // The form has all the Subscription Items that will be invoiced
    // At the next billing cycle
    const { formStore, subscriptionStore } = this.props;
    const { model } = formStore;
    const { subscriptionItems } = model;

    subscriptionItems.forEach(subscriptionItem => {
      selectedOtherProductIds.push(subscriptionItem.otherProductId);
    });

    const { subscription } = subscriptionStore;

    if (!subscription) {
      return selectedOtherProductIds;
    }

    subscription.downgradedSubscriptionItems.forEach(subscriptionItem => {
      if (!selectedOtherProductIds.includes(subscriptionItem.otherProductId)) {
        selectedOtherProductIds.push(subscriptionItem.otherProductId);
      }
    });

    return selectedOtherProductIds;
  }

  @computed
  get creditCardOptions() {
    const { creditCardFormStore } = this.props;
    const { creditCardStore } = creditCardFormStore;
    const { creditCards } = creditCardStore;

    return CreditHelper.getCreditCardOptions(creditCards);
  }

  @action
  setDefaultCreditCard() {
    const { formStore, actualUserStore } = this.props;
    const { model } = formStore;
    const { creditCardId } = model;

    const { t, toastMessageStore } = this.props;
    const formUserDefaultCard = {
      defaultCreditCardId: creditCardId,
    };

    return Promise.all([actualUserStore.save(formUserDefaultCard)]).then(() => {
      toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
    });
  }

  @action
  setQuantityAtPropertySubscriptionItems(index, value) {
    const { formStore } = this.props;
    const { model } = formStore;
    const { subscriptionItems } = model;

    subscriptionItems[index].quantity = value;

    formStore.setProperty("subscriptionItems", subscriptionItems);
  }

  showAddCreditCardForm = () => this.setState({ addCreditCardFormVisible: true });

  hideAddCreditCardForm = () => this.setState({ addCreditCardFormVisible: false });

  @action
  creditCardAddedUpdateList() {
    const { actualUserStore, formStore, creditCardFormStore } = this.props;
    const { creditCardStore } = creditCardFormStore;

    if (creditCardStore.creditCards.length === 0) {
      this.showAddCreditCardForm();
    } else {
      this.hideAddCreditCardForm();
    }

    actualUserStore.load().then(() => {
      const { actualUser } = actualUserStore;

      formStore.setProperty("creditCardId", actualUser.defaultCreditCardId);
    });
  }

  @action
  addSubscriptionItem(otherProductId) {
    const { formStore } = this.props;
    const { model } = formStore;
    const { subscriptionItems } = model;

    if (this.selectedOtherProductIds.includes(otherProductId)) {
      return;
    }

    subscriptionItems.push({
      otherProductId,
      quantity: 1,
    });

    formStore.setProperty("subscriptionItems", subscriptionItems);
  }

  @action
  removeSubscriptionItem(index) {
    const { formStore } = this.props;
    const { model } = formStore;
    const { subscriptionItems } = model;

    subscriptionItems.splice(index, 1);

    formStore.setProperty("subscriptionItems", subscriptionItems);
  }

  render() {
    const {
      t,
      subscriptionStore,
      otherProductStore,
      formStore,
      actualUserStore,
      applicableTaxes,
      subtotal,
      totalAmountWithTaxes,
      quantityOptions,
      onSubmitCallback,
      areFormSubscriptionItemsDirty,
      creditCardFormStore,
      toastMessageStore,
    } = this.props;
    const { otherProducts } = otherProductStore;
    const { isDirty, model, setProperty, getAggregatedError } = formStore;
    const { creditCardId, subscriptionItems } = model;
    const { subscription, errorMessages: subscriptionErrorMessages } = subscriptionStore;
    const { creditCardStore } = creditCardFormStore;
    const { creditCards } = creditCardStore;
    const { addCreditCardFormVisible } = this.state;
    const { isSaving: creditCardSaving } = creditCardFormStore.creditCardStore;
    const { isSaving: actualUserSaving, actualUser } = actualUserStore;

    const isSaving = actualUserSaving || creditCardSaving;

    const serverValidationErrors = { ...subscriptionErrorMessages };

    const validationRules = {
      creditCardId: {
        required: true,
      },
    };
    const validation = getValidationErrors(validationRules, model);

    const { stripeObject } = this;

    return (
      <Ref innerRef={this.ref}>
        <div>
          <LoadingWrapper loading={isSaving} message={t("saving")} />
          <StickyMenu
            contextRef={this.ref}
            hideHeaderWhenIsNotSticky
            headerText={t("billingCentral:createYourSubscription")}
            button={
              <div className="side-page-save-btn-wrap">
                <Button
                  primary
                  icon
                  disabled={
                    !isDirty ||
                    !areFormSubscriptionItemsDirty ||
                    (!actualUser.isUsingExternalInvoicingForSubscriptions && !validation.isValid)
                  }
                  onClick={() => onSubmitCallback()}
                >
                  <Icon name="save" />
                  {t("saveChanges")}
                </Button>
              </div>
            }
          />

          <h2>{t("clientPanelMisc:uiMeet3dPackages")}</h2>
          <p>
            {t("billingCentral:selectSubscriptionPackagesLine1")}
            <br />
            {t("billingCentral:selectSubscriptionPackagesLine2")}
          </p>

          <Card.Group itemsPerRow={5} stackable>
            {otherProductStore.subscriptionOtherProducts.map(otherProduct => (
              <OtherProductCard
                key={otherProduct.otherProductId}
                otherProduct={otherProduct}
                addSubscriptionItemCallback={otherProductId => {
                  this.addSubscriptionItem(otherProductId);
                }}
                selectedOtherProductIds={this.selectedOtherProductIds}
              />
            ))}
          </Card.Group>

          <p style={{ marginTop: "1em" }}>{t("billingCentral:selectSubscriptionPackagesLine3")}</p>

          {subscriptionItems.length === 0 && (
            <Segment textAlign="center">{t("billingCentral:noPackagesSelected")}</Segment>
          )}

          {subscriptionItems.length > 0 && (
            <>
              <Form className="side-page-form-wrap">
                <Table basic="very" style={{ marginTop: "2rem" }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{t("billingCentral:package")}</Table.HeaderCell>
                      <Table.HeaderCell>{t("quantity")}</Table.HeaderCell>
                      <Table.HeaderCell>{t("price")}</Table.HeaderCell>
                      <Table.HeaderCell textAlign="left">{t("total")}</Table.HeaderCell>
                      <Table.HeaderCell />
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {subscriptionItems.map((subscriptionItem, index) => (
                      <SubscriptionEditFormTableRow
                        key={subscriptionItem.otherProductId}
                        existingSubscriptionItem={
                          subscription
                            ? subscription.nextBillingCycleSubscriptionItems.find(
                                existingSubscriptionItem =>
                                  existingSubscriptionItem.otherProductId === subscriptionItem.otherProductId,
                              )
                            : null
                        }
                        otherProduct={otherProducts.find(
                          otherProduct => otherProduct.otherProductId === subscriptionItem.otherProductId,
                        )}
                        index={index}
                        quantity={subscriptionItem.quantity}
                        quantityOptions={quantityOptions}
                        setQuantityAtPropertySubscriptionItems={(selectedIndex, value) =>
                          this.setQuantityAtPropertySubscriptionItems(selectedIndex, value)
                        }
                        removeSubscriptionItemCallback={selectedIndex => {
                          this.removeSubscriptionItem(selectedIndex);
                        }}
                        length={subscriptionItems.length}
                      />
                    ))}
                    {applicableTaxes.length > 0 && (
                      <Table.Row>
                        <Table.Cell />
                        <Table.Cell />
                        <Table.Cell textAlign="right">
                          <strong>{t("subtotal")}</strong>
                        </Table.Cell>
                        <Table.Cell>{CommonHelper.currencyFormat(subtotal)}</Table.Cell>
                        <Table.Cell />
                      </Table.Row>
                    )}
                    {applicableTaxes.map(applicableTax => (
                      <Table.Row key={applicableTax.taxId}>
                        <Table.Cell />
                        <Table.Cell />
                        <Table.Cell textAlign="right">
                          <strong>{applicableTax.name}</strong>
                        </Table.Cell>
                        <Table.Cell>
                          {CommonHelper.currencyFormat(Math.round(subtotal * applicableTax.rate) / 100)}
                        </Table.Cell>
                        <Table.Cell />
                      </Table.Row>
                    ))}
                  </Table.Body>

                  <Table.Footer>
                    <Table.Row>
                      <Table.HeaderCell />
                      <Table.HeaderCell />
                      <Table.HeaderCell textAlign="right">
                        <strong>{t("total")}</strong>
                      </Table.HeaderCell>
                      <Table.HeaderCell>{CommonHelper.currencyFormat(totalAmountWithTaxes)}</Table.HeaderCell>
                      <Table.HeaderCell />
                    </Table.Row>
                  </Table.Footer>
                </Table>

                {/* This is the credit car list */}
                {!actualUser.isUsingExternalInvoicingForSubscriptions &&
                  totalAmountWithTaxes > 0 &&
                  creditCards.length > 0 && (
                    <div className="side-page-input">
                      <div className="ui secondary sticky-menu menu">
                        <div className="item">
                          <h4>{t("billingCentral:creditCard")}</h4>
                        </div>
                        <div className="right menu">
                          {!(creditCardId === "") && creditCardId !== actualUser.defaultCreditCardId && (
                            <div className="clear-floated">
                              <Button
                                onClick={() => this.setDefaultCreditCard(true)}
                                style={{ textAlign: "center", marginTop: "1rem" }}
                                icon="check"
                                content={t("billingCentral:setCreditCardAsDefault")}
                                floated="right"
                              />
                            </div>
                          )}
                          {creditCardId === actualUser.defaultCreditCardId && (
                            <div className="clear-floated">
                              <Button
                                disabled
                                style={{ textAlign: "center", marginTop: "1rem" }}
                                icon="check"
                                content={t("billingCentral:creditCardIsDefault")}
                                floated="right"
                              />
                            </div>
                          )}
                          <div className="clear-floated">
                            <Button
                              onClick={() => this.showAddCreditCardForm()}
                              style={{ textAlign: "center", marginTop: "1rem" }}
                              color="green"
                              icon="add"
                              content={t("add")}
                              floated="right"
                            />
                          </div>
                        </div>
                      </div>
                      <Form.Field
                        control={Select}
                        name="creditCardId"
                        value={model.creditCardId}
                        options={this.creditCardOptions}
                        onChange={(_event, { value }) => setProperty("creditCardId", value)}
                        error={getAggregatedError("creditCardId", validation, serverValidationErrors)}
                        placeholder={`${t("none")}`}
                      />
                    </div>
                  )}
              </Form>

              {!actualUser.isUsingExternalInvoicingForSubscriptions &&
                totalAmountWithTaxes > 0 &&
                creditCards.length === 0 && <NoCreditCardsMessage />}

              {!actualUser.isUsingExternalInvoicingForSubscriptions && addCreditCardFormVisible && (
                <>
                  <div className="side-page-header no-flex" style={{ marginBottom: "1rem" }}>
                    <div className="side-page-header-grid-wrap">
                      <div className="side-page-h1-wrap">
                        <h1>{t("billingCentral:addCreditCard")}</h1>
                      </div>
                    </div>
                  </div>
                  <Card fluid>
                    <Card.Content>
                      <Elements stripe={stripeObject}>
                        <ElementsConsumer>
                          {({ stripe, elements }) => (
                            <CreditCardForm
                              creditCardFormStore={creditCardFormStore}
                              actualUserStore={actualUserStore}
                              stripe={stripe}
                              elements={elements}
                              creditCardCreatedCallback={() => {
                                this.hideAddCreditCardForm();
                                creditCardStore.getAll().then(() => {
                                  this.creditCardAddedUpdateList();
                                });
                              }}
                              toastMessageStore={toastMessageStore}
                            />
                          )}
                        </ElementsConsumer>
                      </Elements>
                    </Card.Content>
                  </Card>
                </>
              )}

              {!actualUser.isUsingExternalInvoicingForSubscriptions &&
                areFormSubscriptionItemsDirty &&
                totalAmountWithTaxes > 0 &&
                creditCards.length > 0 &&
                creditCardId === "" && <NoCreditCardSelectedMessage />}

              {!actualUser.isUsingExternalInvoicingForSubscriptions &&
                totalAmountWithTaxes > 0 &&
                actualUser.defaultCreditCardId === null && <NoDefaultCreditCardMessage />}
            </>
          )}

          <h4>{t("billingCentral:changeWithinTheSameTerm")}</h4>
          <p>{t("billingCentral:changeWithinTheSameTermDescription")}</p>

          <h4>{t("billingCentral:changeForADowngradePackage")}</h4>
          <p>{t("billingCentral:changeForADowngradePackageDescription")}</p>
        </div>
      </Ref>
    );
  }
}

SubscriptionEditForm.propTypes = {
  t: PropTypes.func.isRequired,
  subscriptionStore: PropTypes.instanceOf(SubscriptionStore),
  otherProductStore: PropTypes.instanceOf(OtherProductStore),
  formStore: PropTypes.instanceOf(FormStore),
  creditCardFormStore: PropTypes.instanceOf(CreditCardFormStore),
  actualUserStore: PropTypes.oneOfType([PropTypes.instanceOf(ProviderStore), PropTypes.instanceOf(CustomerStore)]),
  onSubmitCallback: PropTypes.func.isRequired,
  quantityOptions: PropTypes.array.isRequired,
  applicableTaxes: PropTypes.array.isRequired,
  subtotal: PropTypes.number.isRequired,
  totalAmountWithTaxes: PropTypes.number.isRequired,
  areFormSubscriptionItemsDirty: PropTypes.bool.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
  stripeApiPublishableKey: PropTypes.string.isRequired,
};

export default withTranslation()(SubscriptionEditForm);
